import React, { useCallback, useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import _debounce from 'lodash/debounce';
import _differenceWith from 'lodash/differenceWith';
import _isNil from 'lodash/isNil';

import { ICustomApiList, useCustomApiList, useExternalApisByIds } from 'api/customApi';

export const CustomApiSelect = ({
  mode = null,
  placeholder = 'Please select an API',
  preSelected = false,
  value,
  onOptionSelect,
  ...props
}) => {
  const [searchString, setSearchString] = useState<string | null>(null);
  const [preselectedItem, setPreselectedItem] = useState<ICustomApiList[]>([]);
  const [items, setItems] = useState<ICustomApiList[]>([]);
  const [page, setPage] = useState<number>(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: selectedCustomApis } = useExternalApisByIds(selectedIds);

  const { data, meta, isLoading, error } = useCustomApiList({
    search_string: searchString,
    state: 'active',
    page,
    module_type: 'reward_redemption_api'
  });

  const onSearch = useCallback(val => {
    setItems([]);
    setSearchString(val);
  }, []);
  const onSelect = useCallback(
    value => {
      setSearchString(null);
      const selectedOption =
        items.find(item => item.id === value) || preselectedItem.find(item => item.id === value);
      if (selectedOption && onOptionSelect) {
        onOptionSelect(selectedOption);
      }
    },
    [items, preselectedItem, onOptionSelect]
  );

  useEffect(() => {
    if (data?.length) {
      setItems(oldItems => [...oldItems, ...data]);
    }
  }, [data]);

  useEffect(() => {
    const values: number[] = Array.isArray(value)
      ? value
      : _isNil(value) || value === ''
      ? []
      : [value];

    setSelectedIds(values);
  }, [value]);

  useEffect(() => {
    if (selectedCustomApis) {
      setPreselectedItem(selectedCustomApis);
    } else if (selectedIds.length === 0) {
      setPreselectedItem([]);
    }
  }, [selectedCustomApis, selectedIds.length]);

  const handleScroll = e => {
    const { scrollTop, offsetHeight, scrollHeight } = e.target;
    const totalPage = meta?.total_pages || 0;
    if (!isLoading && scrollTop + offsetHeight >= scrollHeight - 500 && page < totalPage) {
      setPage(value => value + 1);
    }
  };

  if (error) return null;

  return (
    <Select
      showSearch
      value={value}
      data-testid="customApi-select"
      loading={isLoading}
      placeholder={isLoading ? 'Loading...' : placeholder}
      style={{ width: '100%' }}
      mode={mode}
      onSearch={_debounce(onSearch, 400)}
      onSelect={onSelect}
      onPopupScroll={handleScroll}
      {...props}
      onBlur={e => {
        setSearchString(null);
        e.preventDefault();
      }}
      filterOption={false}
      notFoundContent={isLoading ? <Spin size="small" /> : null}
    >
      {preselectedItem.map(item => (
        <Select.Option key={Math.floor(1000 + Math.random() * 9000)} value={item.id}>
          {item.name} (ID: {item.id})
        </Select.Option>
      ))}
      {_differenceWith(items, preselectedItem, (x, y) => x.id === y.id).map(option => (
        <Select.Option key={Math.floor(1000 + Math.random() * 9000)} value={option.id}>
          {option.name} (ID: {option.id})
        </Select.Option>
      ))}
    </Select>
  );
};
